import { TFunction } from "i18next";

const getTransactionDescription = (t: TFunction, _text?: string): string => {
	if (typeof _text !== "string") return "";
	const text = _text.trim();

	const cleanPlaceholders = (value: string) =>
		value
			// remove empty quoted placeholders
			.replace(/\s*""/g, "")
			// remove empty parentheses
			.replace(/\s*\(\s*\)/g, "")
			// collapse double spaces
			.replace(/\s{2,}/g, " ")
			.trim();

	const commissionMatch =
		/^(Refund |Surcharge )?Commission fee '([^']+)' for order '([^']+)' \(([^)]+)\)$/i.exec(
			text,
		);
	if (commissionMatch) {
		const [, actionRaw, planName, orderNumber, orderType] = commissionMatch;
		const action = actionRaw?.trim().toLowerCase();
		const localizedOrderType =
			orderType &&
			t(`transactionDescription.orderTypes.${orderType}`, {
				defaultValue: orderType,
			});

		const commissionKeys: Record<string, string> = {
			refund: "transactionDescription.executorCommissionRefund",
			surcharge: "transactionDescription.executorCommissionSurcharge",
			default: "transactionDescription.executorCommission",
		};

		const key =
			action && commissionKeys[action]
				? commissionKeys[action]
				: commissionKeys.default;

		const result = t(key, {
			planName,
			orderNumber,
			orderType: localizedOrderType,
		});

		return result;
	}

	const discountedMatch =
		/^Recalc discounted commission '([^']+)', from (.+?) to (.+?) \(([^)]+)\)$/i.exec(
			text,
		);
	if (discountedMatch) {
		const [, planName, from, to, orderType] = discountedMatch;
		const localizedOrderType =
			orderType &&
			t(`transactionDescription.orderTypes.${orderType}`, {
				defaultValue: orderType,
			});

		const result = t(
			"transactionDescription.executorDiscountedCommissionRecalc",
			{
				planName,
				from,
				to,
				orderType: localizedOrderType,
			},
		);

		return result;
	}

	const subscriptionMatch =
		/^(Refund|Charge|Surcharge) subscription fee '([^']+)', from (.+?) to (.+?)(?: \((.+)\))?$/i.exec(
			text,
		);
	if (subscriptionMatch) {
		const [, actionRaw, planName, from, to, rateNameRaw] =
			subscriptionMatch;
		const action = actionRaw.toLowerCase();
		const rateName = rateNameRaw ? ` (${rateNameRaw})` : "";

		const subscriptionKeys: Record<string, string> = {
			refund: "transactionDescription.executorSubscriptionRefund",
			charge: "transactionDescription.executorSubscriptionCharge",
			surcharge: "transactionDescription.executorSubscriptionSurcharge",
		};

		const key = subscriptionKeys[action];
		if (!key) return _text;

		const result = t(key, {
			planName,
			from,
			to,
			rateName,
		});

		return result;
	}

	// Hold/withdraw operations (support old logs without order data)
	const holdMappings: Array<{
		pattern: RegExp;
		key: string;
	}> = [
		{
			pattern:
				/^Confirm part of the hold(?: for order '([^']+)')?(?: \(([^)]+)\))?$/i,
			key: "transactionDescription.confirmPartOfHold",
		},
		{
			pattern:
				/^Confirm all of the hold(?: for order '([^']+)')?(?: \(([^)]+)\))?$/i,
			key: "transactionDescription.confirmAllOfHold",
		},
		{
			pattern:
				/^Withdraw remaining order amount(?: for order '([^']+)')?(?: \(([^)]+)\))?$/i,
			key: "transactionDescription.withdrawRestAmount",
		},
		{
			pattern:
				/^Withdraw rest amount(?: for order '([^']+)')?(?: \(([^)]+)\))?$/i,
			key: "transactionDescription.withdrawRestAmount",
		},
		{
			pattern:
				/^Write-off of the order price if the customer refuses(?: for order '([^']+)')?(?: \(([^)]+)\))?$/i,
			key: "transactionDescription.writeOffOnCustomerRefuse",
		},
	];

	for (const { pattern, key } of holdMappings) {
		const match = pattern.exec(text);
		if (match) {
			const [, orderNumber, orderTypeRaw] = match;
			const localizedOrderType =
				orderTypeRaw &&
				t(`transactionDescription.orderTypes.${orderTypeRaw}`, {
					defaultValue: orderTypeRaw,
				});

			const hasOrderData = Boolean(orderNumber) || Boolean(orderTypeRaw);

			const result = hasOrderData
				? t(key, {
						orderNumber: orderNumber || "",
						orderType: localizedOrderType || "",
				  })
				: t(key, {
						orderNumber: "",
						orderType: "",
				  });

			return cleanPlaceholders(result);
		}
	}

	const archiveEditBalanceDeltaMatch =
		/^Archive edit delta (charged|refunded) to customer balance orderId=(\d+) target=([-\d.]+) before=([-\d.]+) reason=(\S+)$/i.exec(
			text,
		);
	if (archiveEditBalanceDeltaMatch) {
		const [, action, orderId, targetAmount, beforeAmount, reasonRaw] =
			archiveEditBalanceDeltaMatch;
		const reason = t(
			`transactionDescription.archiveEditDeltaReasons.${reasonRaw}`,
			{ defaultValue: reasonRaw },
		);
		const key =
			action.toLowerCase() === "charged"
				? "transactionDescription.archiveEditDeltaChargedToCustomerBalance"
				: "transactionDescription.archiveEditDeltaRefundedToCustomerBalance";

		return t(key, {
			orderId,
			targetAmount,
			beforeAmount,
			reason,
		});
	}

	const exactDescriptionKeys: Array<{
		pattern: RegExp;
		key: string;
	}> = [
		{
			pattern: /^discount_executor_compensation$/i,
			key: "transactionDescription.discountExecutorCompensation",
		},
		{
			pattern: /^Refund customer bonuses back$/i,
			key: "transactionDescription.refundCustomerBonuses",
		},
		{
			pattern: /^Hold customer bonuses$/i,
			key: "transactionDescription.holdCustomerBonuses",
		},
		{
			pattern: /^Counterparty payment$/i,
			key: "transactionDescription.counterpartyPayment",
		},
		{
			pattern: /^Refund counterparty payment$/i,
			key: "transactionDescription.refundCounterpartyPayment",
		},
		{
			pattern: /^Compensation to the agent for order$/i,
			key: "transactionDescription.agentOrderCompensation",
		},
		{
			pattern: /^Refund agent payment$/i,
			key: "transactionDescription.refundAgentPayment",
		},
		{
			pattern: /^Registration payment$/i,
			key: "transactionDescription.registrationPayment",
		},
		{
			pattern: /^Invoice by customer$/i,
			key: "transactionDescription.invoiceByCustomer",
		},
		{
			pattern: /^Withdraw customer card$/i,
			key: "transactionDescription.withdrawCustomerCard",
		},
		{
			pattern: /^Hold Amount \(rebill saved card\)$/i,
			key: "transactionDescription.holdAmountRebillSavedCard",
		},
		{
			pattern: /^Confirm hold \(capture\)$/i,
			key: "transactionDescription.confirmHoldCapture",
		},
		{
			pattern: /^Refund hold remainder$/i,
			key: "transactionDescription.refundHoldRemainder",
		},
		{
			pattern: /^Confirm hold error, withdraw main$/i,
			key: "transactionDescription.confirmHoldErrorWithdrawMain",
		},
		{
			pattern: /^Card error withdraw main$/i,
			key: "transactionDescription.cardErrorWithdrawMain",
		},
		{
			pattern: /^minus main - hold declined$/i,
			key: "transactionDescription.minusMainHoldDeclined",
		},
		{
			pattern: /^Withdraw executor card replenishment executor main$/i,
			key: "transactionDescription.withdrawExecutorCardReplenishment",
		},
		{
			pattern: /^Order withdraw all summ$/i,
			key: "transactionDescription.orderWithdrawAllSumm",
		},
		{
			pattern: /^Archive edit card delta settlement$/i,
			key: "transactionDescription.archiveEditCardDeltaSettlement",
		},
		{
			pattern: /^Refund captured card payment on order re-close$/i,
			key: "transactionDescription.refundCapturedCardPaymentOnReclose",
		},
		{
			pattern:
				/^Archive edit customer balance settle reversed on order re-close$/i,
			key: "transactionDescription.archiveEditCustomerBalanceReversedOnReclose",
		},
		{
			pattern: /^Payment to the fleet for a completed order$/i,
			key: "transactionDescription.paymentToFleetForCompletedOrder",
		},
		{
			pattern: /^Refund fleet payment$/i,
			key: "transactionDescription.refundFleetPayment",
		},
	];

	for (const { pattern, key } of exactDescriptionKeys) {
		if (pattern.test(text)) {
			const localized = t(key);
			// Missing i18n key → i18next returns the key path; keep English then.
			if (localized && localized !== key) return localized;
			return text;
		}
	}

	return text || "";
};

export default getTransactionDescription;
